import type { FC, ReactNode } from 'react'

import {
    CarFront,
    CreditCard,
    KeyRound,
    MapPin,
    Navigation,
    SquareParking
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button'
import { Container } from '@/components/generals/container'
import { OpeningStatusLine } from '@/components/generals/opening-status'
import { SectionLabel } from '@/components/generals/section-label'
import { formatClock } from '@/lib/format'
import { useOpeningStatus } from '@/hooks/use-opening-status'

import { PRACTICAL_INFOS } from '@/constants/content'
import { directions, garage, openingHours } from '@/constants/garage'

const INFO_ICONS = {
    parking: SquareParking,
    key: KeyRound,
    car: CarFront,
    card: CreditCard
}

const HoursTable: FC = (): ReactNode => {
    const status = useOpeningStatus()

    return (
        <table className='w-full text-[0.9375rem]'>
            <caption className='sr-only'>
                Horaires d’ouverture de l’atelier
            </caption>

            <tbody>
                {openingHours.map(day => {
                    const isToday = status?.today === day.day

                    return (
                        <tr key={day.day} className={cn('border-b border-border', isToday && 'bg-card')}>
                            <th scope='row' className={cn('py-3 pl-3 text-left font-medium', isToday ? 'font-semibold' : 'text-foreground/85')}>
                                {day.label}

                                {isToday && (
                                    <span className='label-mono ml-2 rounded-sm bg-ink px-1.5 py-0.5 text-[0.625rem] text-chalk'>
                                        Aujourd’hui
                                    </span>
                                )}
                            </th>

                            <td className={cn('py-3 pr-3 text-right font-mono text-sm tabular-nums', isToday ? 'text-foreground' : 'text-muted-foreground')}>
                                {day.open != null && day.close != null
                                    ? `${formatClock(day.open)} · ${formatClock(day.close)}`
                                    : 'Fermé'}
                            </td>
                        </tr>
                    )
                })}
            </tbody>
        </table>
    )
}

export const ContactSection: FC = (): ReactNode => {
    return (
        <section id='contact' aria-labelledby='contact-title' className='py-20 lg:py-32'>
            <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10 xl:gap-16'>
                <div className='lg:col-span-5'>
                    <SectionLabel index='06'>Accès et horaires</SectionLabel>

                    <h2 id='contact-title' className='heading mt-5 text-title'>
                        Passez à l’atelier, ou appelez Gwenaëlle.
                    </h2>

                    <a
                        href={garage.phone.href}
                        className='heading mt-8 inline-flex min-h-12 items-center text-[clamp(2rem,1.5rem+2vw,2.875rem)] leading-none tracking-[-0.025em] tabular-nums underline decoration-primary decoration-[0.08em] underline-offset-[0.18em] transition-colors duration-150 hover:text-signal-text'
                    >
                        {garage.phone.display}
                    </a>

                    <OpeningStatusLine className='mt-4 text-[0.9375rem]' />

                    <address className='mt-8 flex gap-3 not-italic'>
                        <MapPin aria-hidden='true' className='mt-1 size-5 shrink-0 text-signal-text' />

                        <span>
                            <span className='block font-semibold'>
                                {garage.address.street}
                            </span>

                            <span className='block text-muted-foreground'>
                                {garage.address.postalCode} {garage.address.city} · {garage.address.district}, à 5 minutes du centre-ville
                            </span>
                        </span>
                    </address>

                    <div className='mt-10' id='horaires'>
                        <h3 className='label-mono text-muted-foreground'>
                            Horaires
                        </h3>

                        <div className='mt-3 border-t border-border'>
                            <HoursTable />
                        </div>

                        <p className='mt-3 text-sm text-muted-foreground'>
                            Journée continue, sans coupure le midi. Fermé les jours fériés.
                        </p>
                    </div>
                </div>

                <div className='lg:col-span-7'>
                    <div className='relative overflow-hidden rounded-xl border border-border bg-surface'>
                        <div className='relative aspect-[4/3] sm:aspect-[16/10]'>
                            <iframe
                                title='Plan de situation du Garage Kermarec'
                                src={directions.mapEmbed}
                                loading='lazy'
                                tabIndex={-1}
                                aria-hidden='true'
                                referrerPolicy='no-referrer-when-downgrade'
                                className='pointer-events-none absolute inset-0 size-full grayscale contrast-[1.05]'
                            />

                            <div className='pointer-events-none absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-[calc(100%-6px)] flex-col items-center'>
                                <span className='label-mono whitespace-nowrap rounded-sm bg-ink px-2.5 py-1.5 text-chalk shadow-float'>
                                    Garage Kermarec
                                </span>

                                <span aria-hidden='true' className='h-3 w-px bg-ink' />
                                <span aria-hidden='true' className='size-3 rounded-full border-2 border-chalk bg-primary shadow-card' />
                            </div>
                        </div>

                        <div className='flex flex-col gap-3 border-t border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5'>
                            <p className='text-sm text-muted-foreground'>
                                Parking client gratuit devant l’atelier
                            </p>

                            <div className='grid grid-cols-2 gap-2 sm:flex'>
                                <ButtonLink href={directions.google} target='_blank' rel='noopener noreferrer'>
                                    <Navigation aria-hidden='true' className='size-4' />
                                    Itinéraire
                                    <span className='sr-only'> (Google Maps, nouvel onglet)</span>
                                </ButtonLink>

                                <ButtonLink href={directions.waze} target='_blank' rel='noopener noreferrer' variant='secondary'>
                                    Waze
                                    <span className='sr-only'> (nouvel onglet)</span>
                                </ButtonLink>
                            </div>
                        </div>
                    </div>

                    <p className='mt-2 text-xs text-muted-foreground'>
                        Fond de carte ©{' '}
                        <a href={directions.osm} target='_blank' rel='noopener noreferrer' className='underline underline-offset-2'>
                            contributeurs OpenStreetMap
                        </a>
                    </p>

                    <ul className='mt-10 grid gap-x-8 sm:grid-cols-2'>
                        {PRACTICAL_INFOS.map(info => {
                            const Icon = INFO_ICONS[info.icon]

                            return (
                                <li key={info.title} className='flex gap-4 border-b border-border py-5'>
                                    <Icon aria-hidden='true' className='mt-0.5 size-5 shrink-0' />

                                    <div>
                                        <p className='font-semibold'>
                                            {info.title}
                                        </p>

                                        <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>
                                            {info.text}
                                        </p>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            </Container>
        </section>
    )
}
